<template>
    <div class="d-flex flex-column" style="height: 100%; min-height: 0;">
        <!-- Tabs for filtering -->
        <v-tabs v-model="activeTab" class="flex-grow-0 flex-shrink-0 mx-0 mb-2"
                color="primary">
            <v-tab v-for="tab in allTabs" :key="tab.id" class="text-none">
                {{ tab.label }}
            </v-tab>
        </v-tabs>

        <!-- Editor component with scroll container inside -->
        <EventTimelineEditor
            ref="editor"
            :tasks="tasks"
            :task="task"
            :event="event"
            @task-updated="$emit('task-updated', $event)"
            @update:task="$emit('update:task', $event)">

            <!-- Timeline content with scroll container -->
            <EventTimelineScrollContainer
                ref="scrollContainer"
                :timeline-items="timelineItems"
                :task="task"
                :read-only="isTaskReadOnly"
                :enable-long-press="enableLongPress"
                @edit="handleEdit"
                @delete="handleDelete"
                @edit-travel-time="handleEditTravelTime"
                @edit-on-site-time="handleEditOnSiteTime"
                @file-open="handleFileOpen"
                @action="handleTimelineAction" />

        </EventTimelineEditor>

        <!-- Comment section fixed at bottom -->
        <div class="flex-shrink-0 mt-3 mx-3 comment-section">
            <v-row align="center" no-gutters>
                <v-col>
                    <v-textarea dense v-model='comment' append-outer-icon='mdi-send' @click:append-outer="sendComment"
                        clearable filled type="text" @click:clear="comment = null" v-if="isTaskOpen" outlined
                        :label="commentLabel" @keydown.enter.exact.prevent="sendComment" :prepend-icon="channelIcon"
                        @keydown.enter.shift.exact.prevent="addLineToComment" rows="2" auto-grow hide-details="auto" />
                </v-col>
            </v-row>
        </div>
    </div>
</template>

<script>
import { firstAssigneeUser, UserTaskType, isTaskOpen } from '@/store/modules/task'
import UserTask from '@/store/modules/task'
import { getPrimaryEventTask } from '@/store/modules/task'
import { convertTasksToTimelineItems, sendMessage, TimelineFilterCallbacks } from '@/util/timeline'
import { sync } from 'vuex-pathify'
import EventTimelineScrollContainer from './EventTimelineScrollContainer.vue'
import EventTimelineEditor from './EventTimelineEditor.vue'

export default {
    name: "EventTimeline",

    components: {
        EventTimelineScrollContainer,
        EventTimelineEditor
    },

    props: {
        tasks: {
            type: Array,
            default: () => []
        },
        event: {
            type: Object,
            required: true
        },
        dispatcherTask: {
            type: Object,
            default: null
        },
        draftMessages: {
            type: Array,
            default: () => []
        },
        // New props for alternative editing methods
        enableLongPress: {
            type: Boolean,
            default: true
        }
    },

    data() {
        return {
            activeTab: 0,
            comment: null
        }
    },

    computed: {
        currentUser: sync('user.profile'),

        // Configuration for general tabs based on event status
        tabConfig() {
            return ['summary', 'full']
        },

        // Generate general tabs for dispatcher/general task
        generalTabs() {
            const generalTask = getPrimaryEventTask(this.tasks)
            if (!generalTask) {
                return []
            }

            const labels = {
                summary: 'Podsumowanie',
                full: 'Ogólny'
            }

            return this.tabConfig.map(mode => ({
                id: `general-${mode}`,
                task: generalTask,
                mode: mode,
                label: labels[mode],
                type: 'general'
            }))
        },

        // All tabs (general + patrol tabs)
        allTabs() {
            const generalTask = getPrimaryEventTask(this.tasks)
            const patrolTabs = this.tasks
                .filter(t => t.task_type !== UserTaskType.DISPATCHER)
                .filter(t => !generalTask || t.id !== generalTask.id)
                .map(task => ({
                    id: `patrol-${task.id}`,
                    task: task,
                    mode: 'patrol',
                    label: this.getTaskTabLabel(task),
                    type: task.task_type === UserTaskType.SERVICE ? 'service' : 'patrol'
                }))

            return [...this.generalTabs, ...patrolTabs]
        },

        // Current tab configuration
        currentTabConfig() {
            if (this.allTabs.length === 0) {
                return null
            }
            return this.allTabs[this.activeTab] || null
        },

        // Current filter mode
        filterMode() {
            return this.currentTabConfig?.mode || 'full'
        },

        isTaskReadOnly() {
            return UserTask.isReadOnly(this.task)
        },

        isTaskOpen() {
            return isTaskOpen(this.task)
        },

        task() {
            if (this.tasks.length === 0 || !this.currentTabConfig) {
                return null
            }
            return this.currentTabConfig.task
        },

        timelineItems() {
            if (!this.task) {
                return []
            }

            // Create filter callback based on current mode
            let filterCallback = null

            switch (this.filterMode) {
                case 'full':
                    // Show all items
                    filterCallback = () => true
                    break

                case 'summary':
                    // Show summary-relevant items across general timeline
                    filterCallback = TimelineFilterCallbacks.summaryRelevantItems
                    break

                case 'patrol':
                    // Show only items for the selected patrol task
                    filterCallback = (task) => task.id === this.task.id
                    break

                default:
                    filterCallback = () => true
            }

            // Convert tasks to timeline items with filtering
            const items = convertTasksToTimelineItems(this.tasks, filterCallback)

            // Filter draft messages based on current mode
            let filteredDraftMessages = []
            if (this.filterMode === 'patrol') {
                // Show only draft messages for the selected patrol task
                filteredDraftMessages = this.draftMessages.filter(draft => draft.taskId === this.task.id)
            } else if (this.filterMode === 'summary') {
                // Show only draft messages for general task (dispatcher/service fallback)
                const generalTask = getPrimaryEventTask(this.tasks)
                if (generalTask) {
                    filteredDraftMessages = this.draftMessages.filter(draft => draft.taskId === generalTask.id)
                }
            } else {
                // For 'full', show all draft messages from general tabs
                const generalTaskIds = this.generalTabs.map(tab => tab.task.id)
                filteredDraftMessages = this.draftMessages.filter(draft => generalTaskIds.includes(draft.taskId))
            }

            // Combine timeline items with draft messages and sort by creation time
            const allItems = [...items, ...filteredDraftMessages]
            allItems.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))

            return allItems
        },

        commentLabel() {
            if (!this.currentTabConfig) {
                return 'Wiadomość ogólna'
            }

            if (this.currentTabConfig.type === 'general') {
                return 'Wiadomość ogólna'
            } else {
                return `Wiadomość do: ${this.currentTabConfig.label}`
            }
        },

        channelIcon() {
            const task = this.task
            if (task?.task_type === UserTaskType.DISPATCHER) {
                return 'mdi-forum'
            } else if (task?.task_type === UserTaskType.PATROL) {
                return 'mdi-car'
            } else {
                return 'mdi-account'
            }
        }
    },
    watch: {
        timelineItems: {
            handler() {
                // Timeline items changed - scroll container will handle auto-scroll
            },
            deep: true
        },
        draftMessages: {
            handler() {
                // Draft messages changed - scroll container will handle auto-scroll
            },
            deep: true
        },
        allTabs(tabs) {
            if (tabs.length === 0) {
                this.activeTab = 0
                return
            }

            if (this.activeTab >= tabs.length) {
                this.activeTab = tabs.length - 1
            }
        },
        // Watch for active tab changes
        activeTab() {
            // Timeline items are computed automatically, no need for store updates
        }
    },

    methods: {
        // Editor delegation methods
        handleEdit(event) {
            this.$refs.editor.handleEdit(event)
        },

        handleDelete(event) {
            this.$refs.editor.handleDelete(event)
        },

        handleEditTravelTime(event) {
            this.$refs.editor.handleEditTravelTime(event)
        },

        handleEditOnSiteTime(event) {
            this.$refs.editor.handleEditOnSiteTime(event)
        },

        getChannelUser() {
            return firstAssigneeUser(this.task)
        },

        getTaskTabLabel(task) {
            if (task?.task_type === UserTaskType.SERVICE) {
                return task?.description || 'Serwis'
            }

            const user = firstAssigneeUser(task)
            if (user && task.assignees && task.assignees.length > 0) {
                const assignee = task.assignees[0]
                return this.$options.filters.userName(assignee.user)
            }
            return `Patrol ${task?.id || 'N/A'}`
        },

        async sendComment() {
            if (!this.comment || this.comment.trim().length === 0) {
                return
            }

            try {
                // Create message using timeline utility
                const draftMessage = sendMessage(this.task, this.comment, this.currentUser)

                // Determine patrol based on tab type
                let patrol = null
                if (this.currentTabConfig && this.currentTabConfig.type === 'patrol') {
                    patrol = this.currentTabConfig.task
                }

                const payload = {
                    comment: this.comment,
                    activeTab: this.activeTab,
                    patrol: patrol,
                    task: this.task,
                    draftMessage: draftMessage
                }

                // Emit event to parent component
                this.$emit('send-comment', payload)

                // Clear comment after sending
                this.comment = null

                // Auto-scroll to show the new message
                this.$nextTick(() => {
                    if (this.$refs.scrollContainer) {
                        this.$refs.scrollContainer.scrollToBottomExternal()
                    }
                })

            } catch (error) {
                console.error('Error sending comment:', error)
                this.$emit('error', {
                    message: 'Błąd podczas wysyłania wiadomości',
                    error
                })
            }
        },

        addLineToComment() {
            this.comment = (this.comment || '') + '\n'
        },

        handleFileOpen(file) {
            this.$emit('file-open', file)
        },

        handleTimelineAction({ action, item }) {
            this.$emit('timeline-action', { action, item })
        }
    }
}
</script>

<style scoped>
.comment-section {
    /* Ensure comment section adapts to font size properly */
    min-height: max(80px, 5rem);
    height: auto;
}

/* Ensure the main container fills available space properly */
.d-flex.flex-column {
    height: 100%;
    min-height: 0;
}
</style>
